import React from 'react';
import {
  Link2,
  FileText,
  MessageCircle,
  Upload,
  Sparkles,
  BookOpen,
  Monitor,
  Library,
  WalletCards,
  Calendar,
} from 'lucide-react';
import { SHOW_QUIZ_FEATURES } from '../config/features';
import './LandingStudyShowcase.css';

const LandingStudyShowcase = () => {
  const steps = [
    {
      icon: Link2,
      title: '1. Add your material',
      body: 'Upload a PDF or paste a YouTube link. Everything stays in one workspace.',
    },
    {
      icon: FileText,
      title: '2. Get structured notes',
      body: 'Summaries, topics, and highlights generated from what you added—ready to edit.',
    },
    {
      icon: MessageCircle,
      title: '3. Study and ask questions',
      body: 'Chat with your content, generate flashcards, and practice so it actually sticks.',
    },
  ];

  const featureCards = [
    {
      icon: Upload,
      title: 'Upload or link',
      subtitle: 'PDFs • YouTube • documents',
      body: 'Bring in what you are already using for class or self-study.',
    },
    {
      icon: Sparkles,
      title: 'Notes & structure',
      subtitle: 'Summaries and key ideas',
      body: 'Turn long readings and lectures into something you can skim before a test.',
    },
    {
      icon: MessageCircle,
      title: 'AI chat',
      subtitle: 'Ask your material anything',
      body: 'Answers grounded in what you uploaded—not generic filler.',
    },
    {
      icon: Monitor,
      title: 'Runs in your browser',
      subtitle: 'Sign in and go',
      body: 'No separate desktop app required for core study flows.',
      wide: true,
    },
    {
      icon: Library,
      title: 'Library in one place',
      subtitle: 'Quizzes, flashcards, notes',
      body: 'Jump between sets and saved notes without losing context.',
      wide: true,
    },
    {
      icon: WalletCards,
      title: 'Flashcards',
      subtitle: 'Generated from your sources',
      body: 'Practice fronts and backs sourced from your own uploads and links.',
    },
    SHOW_QUIZ_FEATURES
      ? {
          icon: BookOpen,
          title: 'Quizzes',
          subtitle: 'Multiple choice from your docs',
          body: 'Quick checks to see what you remember before the real exam.',
        }
      : {
          icon: Calendar,
          title: 'Study plan',
          subtitle: 'Roadmap your week',
          body: 'Outline what to review next instead of cramming blindly.',
        },
    {
      icon: BookOpen,
      title: 'Notes you own',
      subtitle: 'Edit and save',
      body: 'Tweak summaries and keep a scratchpad alongside each note.',
    },
  ];

  return (
    <div className="studyflow" id="features">
      <section className="studyflow-section studyflow-section--how" aria-labelledby="studyflow-how-heading">
        <p className="studyflow-badge">How it works</p>
        <h2 id="studyflow-how-heading" className="studyflow-heading">
          StudySpace keeps it simple.
        </h2>
        <div className="studyflow-steps">
          {steps.map(({ icon: Icon, title, body }) => (
            <div key={title} className="studyflow-step">
              <div className="studyflow-step-icon-wrap" aria-hidden>
                <Icon className="studyflow-step-icon" strokeWidth={1.75} size={26} />
              </div>
              <h3 className="studyflow-step-title">{title}</h3>
              <p className="studyflow-step-body">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="studyflow-section studyflow-section--features" aria-labelledby="studyflow-features-heading">
        <p className="studyflow-badge">Features</p>
        <h2 id="studyflow-features-heading" className="studyflow-heading">
          Capture, organize, and study with less friction.
        </h2>
        <p className="studyflow-lead">
          Built around uploads and links you already have—not a generic chatbot with no memory of your files.
        </p>
        <div className="studyflow-features-grid">
          {featureCards.map(({ icon: Icon, title, subtitle, body, wide }) => (
            <article
              key={title}
              className={`studyflow-fcard${wide ? ' studyflow-fcard--wide' : ''}`}
            >
              <div className="studyflow-fcard-icon" aria-hidden>
                <Icon strokeWidth={1.5} size={40} />
              </div>
              <h3 className="studyflow-fcard-title">{title}</h3>
              <p className="studyflow-fcard-subtitle">{subtitle}</p>
              <p className="studyflow-fcard-body">{body}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default LandingStudyShowcase;
